import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, Clock, Users } from 'lucide-react';
import { DayTemplateShift, Employee } from '@/types';
import TemplateShiftEditor from './TemplateShiftEditor';

interface EditDayTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
  templateName: string;
  onNameChange: (name: string) => void;
  shifts: DayTemplateShift[];
  onShiftsChange: (shifts: DayTemplateShift[]) => void;
  isSaving?: boolean;
  businessId?: string | null;
  employees?: Employee[];
}

const EditDayTemplateDialog: React.FC<EditDayTemplateDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  templateName,
  onNameChange,
  shifts,
  onShiftsChange,
  isSaving = false,
  businessId,
  employees = [],
}) => {
  // Check if any shifts have employee assignments
  const hasEmployeeAssignments = shifts.some(shift => shift.employeeId);
  const [saveEmployeeAssignments, setSaveEmployeeAssignments] = useState(hasEmployeeAssignments);
  const handleSave = async () => {
    try {
      await onSave();
    } catch (error) {
      // Error handled in parent component - do not close dialog
      console.error('Error saving template:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-hidden" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Edit Day Template
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1">
          {/* Interactive shift editor */}
          <div className="space-y-2">
            <div className="overflow-x-auto">
              <TemplateShiftEditor
                shifts={shifts}
                onShiftsChange={onShiftsChange}
                businessId={businessId}
                employees={employees}
                saveEmployeeAssignments={saveEmployeeAssignments}
              />
            </div>
          </div>

          {/* Form fields */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="Enter template name"
                disabled={isSaving}
                maxLength={50}
              />
            </div>

            {/* Employee assignments toggle */}
            {businessId && employees.length > 0 && (
              <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-md border border-border">
                <Checkbox
                  id="save-employees"
                  checked={saveEmployeeAssignments}
                  onCheckedChange={(checked) => setSaveEmployeeAssignments(checked as boolean)}
                  disabled={isSaving}
                />
                <div className="flex-1">
                  <Label htmlFor="save-employees" className="flex items-center gap-2 cursor-pointer font-medium">
                    <Users className="h-4 w-4" />
                    Save employee assignments with template
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    When enabled, shifts will be pre-assigned to specific employees when applying this template
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !templateName.trim() || shifts.length === 0}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditDayTemplateDialog;
